/**
 * Browser-side preference store hook
 * Provides IndexedDB-backed read/write access to preferences
 * Each identity gets its own isolated database
 */

import { useCallback, useEffect, useState, useRef } from 'react';
import { PreferencePayload } from '@maco144/shared';

const DB_NAME_PREFIX = 'rising-sun-preferences';
const STORE_NAME = 'preferences';
const DB_VERSION = 1;

export interface PreferenceStoreState {
  isInitialized: boolean;
  isLoading: boolean;
  error: string | null;
}

/**
 * Hook for reading/writing preferences to IndexedDB
 * Stores preferences per identity for isolation
 */
export function usePreferenceStore(userId?: string) {
  const [state, setState] = useState<PreferenceStoreState>({
    isInitialized: false,
    isLoading: false,
    error: null,
  });

  // Keep reference to database for cleanup
  const dbRef = useRef<IDBDatabase | null>(null);

  // Get database name for this user
  const getDbName = useCallback((uid: string): string => {
    return `${DB_NAME_PREFIX}-${uid}`;
  }, []);

  // Open or get IndexedDB connection
  const getDB = useCallback(
    async (uid: string): Promise<IDBDatabase> => {
      if (dbRef.current) {
        return dbRef.current;
      }

      return new Promise((resolve, reject) => {
        const request = indexedDB.open(getDbName(uid), DB_VERSION);

        request.onupgradeneeded = (event) => {
          const db = (event.target as IDBOpenDBRequest).result;
          // Create preferences store if it doesn't exist
          if (!db.objectStoreNames.contains(STORE_NAME)) {
            db.createObjectStore(STORE_NAME);
          }
        };

        request.onsuccess = () => {
          const db = request.result;
          dbRef.current = db;
          resolve(db);
        };

        request.onerror = () => {
          reject(new Error(`Failed to open IndexedDB: ${request.error}`));
        };
      });
    },
    [getDbName]
  );

  // Initialize store for a user
  const initialize = useCallback(
    async (uid: string) => {
      if (!uid) {
        setState((prev) => ({ ...prev, error: 'No user ID provided' }));
        return;
      }

      setState((prev) => ({ ...prev, isLoading: true }));
      try {
        await getDB(uid);
        setState((prev) => ({
          ...prev,
          isInitialized: true,
          isLoading: false,
          error: null,
        }));
      } catch (err) {
        const error = err instanceof Error ? err.message : 'Unknown error';
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error,
        }));
      }
    },
    [getDB]
  );

  // Initialize when userId changes
  useEffect(() => {
    if (userId) {
      initialize(userId);
    }
  }, [userId, initialize]);

  // Get a single preference
  const getPreference = useCallback(
    async (key: string): Promise<PreferencePayload | undefined> => {
      if (!userId || !state.isInitialized) return undefined;

      try {
        const db = await getDB(userId);
        return new Promise((resolve, reject) => {
          const tx = db.transaction([STORE_NAME], 'readonly');
          const store = tx.objectStore(STORE_NAME);
          const request = store.get(key);

          request.onsuccess = () => resolve(request.result);
          request.onerror = () => reject(request.error);
        });
      } catch (err) {
        console.error(`Failed to get preference ${key}:`, err);
        return undefined;
      }
    },
    [userId, state.isInitialized, getDB]
  );

  // Get multiple preferences
  const getPreferences = useCallback(
    async (keys: string[]): Promise<Map<string, PreferencePayload>> => {
      if (!userId || !state.isInitialized) return new Map();

      const result = new Map<string, PreferencePayload>();

      try {
        for (const key of keys) {
          const pref = await getPreference(key);
          if (pref) {
            result.set(key, pref);
          }
        }
      } catch (err) {
        console.error('Failed to get preferences:', err);
      }

      return result;
    },
    [userId, state.isInitialized, getPreference]
  );

  // Set a single preference
  const setPreference = useCallback(
    async (key: string, value: unknown): Promise<void> => {
      if (!userId || !state.isInitialized) return;

      try {
        const db = await getDB(userId);

        const payload: PreferencePayload = {
          key,
          value,
          schema: typeof value,
          version: 1,
          timestamp: Date.now(),
        };

        await new Promise<void>((resolve, reject) => {
          const tx = db.transaction([STORE_NAME], 'readwrite');
          const store = tx.objectStore(STORE_NAME);
          const request = store.put(payload, key);

          request.onsuccess = () => resolve();
          request.onerror = () => reject(request.error);
        });
      } catch (err) {
        console.error(`Failed to set preference ${key}:`, err);
      }
    },
    [userId, state.isInitialized, getDB]
  );

  // Set multiple preferences
  const setPreferences = useCallback(
    async (updates: Record<string, unknown>): Promise<void> => {
      if (!userId || !state.isInitialized) return;

      try {
        const db = await getDB(userId);

        await new Promise<void>((resolve, reject) => {
          const tx = db.transaction([STORE_NAME], 'readwrite');
          const store = tx.objectStore(STORE_NAME);

          for (const [key, value] of Object.entries(updates)) {
            const payload: PreferencePayload = {
              key,
              value,
              schema: typeof value,
              version: 1,
              timestamp: Date.now(),
            };
            store.put(payload, key);
          }

          tx.oncomplete = () => resolve();
          tx.onerror = () => reject(tx.error);
        });
      } catch (err) {
        console.error('Failed to set preferences:', err);
      }
    },
    [userId, state.isInitialized, getDB]
  );

  // Delete a preference
  const deletePreference = useCallback(
    async (key: string): Promise<void> => {
      if (!userId || !state.isInitialized) return;

      try {
        const db = await getDB(userId);

        await new Promise<void>((resolve, reject) => {
          const tx = db.transaction([STORE_NAME], 'readwrite');
          const store = tx.objectStore(STORE_NAME);
          const request = store.delete(key);

          request.onsuccess = () => resolve();
          request.onerror = () => reject(request.error);
        });
      } catch (err) {
        console.error(`Failed to delete preference ${key}:`, err);
      }
    },
    [userId, state.isInitialized, getDB]
  );

  // Export all preferences
  const exportAll = useCallback(async (): Promise<Record<string, unknown>> => {
    if (!userId || !state.isInitialized) return {};

    try {
      const db = await getDB(userId);

      return new Promise((resolve, reject) => {
        const tx = db.transaction([STORE_NAME], 'readonly');
        const store = tx.objectStore(STORE_NAME);
        const request = store.getAll();

        request.onsuccess = () => {
          const prefs = request.result as PreferencePayload[];
          const result: Record<string, unknown> = {};
          for (const pref of prefs) {
            result[pref.key] = pref.value;
          }
          resolve(result);
        };

        request.onerror = () => reject(request.error);
      });
    } catch (err) {
      console.error('Failed to export preferences:', err);
      return {};
    }
  }, [userId, state.isInitialized, getDB]);

  // Import preferences
  const importAll = useCallback(
    async (data: Record<string, unknown>): Promise<void> => {
      if (!userId || !state.isInitialized) return;

      try {
        const db = await getDB(userId);

        await new Promise<void>((resolve, reject) => {
          const tx = db.transaction([STORE_NAME], 'readwrite');
          const store = tx.objectStore(STORE_NAME);

          for (const [key, value] of Object.entries(data)) {
            const payload: PreferencePayload = {
              key,
              value,
              schema: typeof value,
              version: 1,
              timestamp: Date.now(),
            };
            store.put(payload, key);
          }

          tx.oncomplete = () => resolve();
          tx.onerror = () => reject(tx.error);
        });
      } catch (err) {
        console.error('Failed to import preferences:', err);
      }
    },
    [userId, state.isInitialized, getDB]
  );

  // Clear all preferences
  const clearAll = useCallback(async (): Promise<void> => {
    if (!userId || !state.isInitialized) return;

    try {
      const db = await getDB(userId);

      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction([STORE_NAME], 'readwrite');
        const store = tx.objectStore(STORE_NAME);
        const request = store.clear();

        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    } catch (err) {
      console.error('Failed to clear preferences:', err);
    }
  }, [userId, state.isInitialized, getDB]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (dbRef.current) {
        dbRef.current.close();
        dbRef.current = null;
      }
    };
  }, []);

  return {
    // State
    isInitialized: state.isInitialized,
    isLoading: state.isLoading,
    error: state.error,

    // Operations
    initialize,
    getPreference,
    getPreferences,
    setPreference,
    setPreferences,
    deletePreference,
    exportAll,
    importAll,
    clearAll,
  };
}

export type UsePreferenceStoreReturn = ReturnType<typeof usePreferenceStore>;
